import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, ListItemText, MenuItem, TextField, Typography } from '@mui/material';
import { SimpleLoading } from './Loading';

export type KeyValue = {
  key: string;
  value: string;
  /** Optional second line in the option, e.g. an email address. */
  description?: string;
};

type DialogSelectProps = {
  loadData: () => Promise<KeyValue[]>;
  initialValue?: string;
  onSelect: Function | undefined;
  title: string;
  /** Shown under the title, e.g. the organisation being changed. */
  subtitle?: string;
  /** Field label, e.g. "Client manager". */
  label?: string;
  /** Label for the empty choice, e.g. "No manager". */
  noneLabel?: string;
  emptyTitle: string;
  open: boolean;
  setOpen: Function;
};

export default function DialogSelect(props: DialogSelectProps) {
  const { onSelect, title, subtitle, label, noneLabel = 'None', emptyTitle, initialValue, open, setOpen, loadData } = props;
  const [dataLoading, setDataLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<KeyValue[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selected, setSelected] = useState<string>('');

  const init = async () => {
    if (loadData) {
      setDataLoading(true);
      setLoadFailed(false);
      try {
        setData(await loadData());
      } catch (e) {
        // e.g. Xero didn't answer: show an error instead of spinning forever
        setData([]);
        setLoadFailed(true);
      } finally {
        setDataLoading(false);
      }
    } else {
      setDataLoading(false);
    }
    setSelected(initialValue || '');
  };

  useEffect(() => {
    init();
  }, [initialValue, loadData]);

  const handleClose = (event: React.SyntheticEvent<unknown>, reason?: string) => {
    if (reason !== 'backdropClick') {
      setOpen(false);
    }
  };

  return (
    <Dialog disableEscapeKeyDown open={open} onClose={handleClose} fullWidth maxWidth="xs" aria-labelledby="dialog-select-title">
      <DialogTitle id="dialog-select-title" sx={{ fontWeight: 600, pb: subtitle ? 0.5 : 2 }}>
        {title}
        {subtitle && (
          <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block', fontWeight: 400 }}>
            {subtitle}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {dataLoading ? (
          <SimpleLoading message={'Loading'} />
        ) : loadFailed ? (
          <Typography color="error" sx={{ pt: 1 }}>
            Couldn't load the options. Please close this and try again.
          </Typography>
        ) : data.length == 0 ? (
          <Typography color="text.secondary" sx={{ pt: 1 }}>
            {emptyTitle || 'Nothing to choose from yet.'}
          </Typography>
        ) : (
          <Box sx={{ pt: 1 }}>
            <TextField
              select
              fullWidth
              size="small"
              label={label || title}
              value={selected}
              onChange={event => setSelected(event.target.value || '')}
              SelectProps={{
                renderValue: (value: unknown) => (value ? data.find(d => d.key === value)?.value || String(value) : <em>{noneLabel}</em>),
                displayEmpty: true,
              }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">
                <em>{noneLabel}</em>
              </MenuItem>
              {data.map(entry => (
                <MenuItem key={entry.key} value={entry.key}>
                  <ListItemText primary={entry.value} secondary={entry.description} sx={{ my: 0 }} />
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button disabled={submitting} onClick={handleClose} color="inherit">
          Cancel
        </Button>
        {data.length > 0 && (
          <Button
            variant="contained"
            disabled={submitting || selected === (initialValue || '')}
            onClick={async () => {
              setSubmitting(true);
              try {
                onSelect && (await onSelect(selected));
              } finally {
                setOpen(false);
                setSubmitting(false);
              }
            }}
          >
            Save {submitting && <CircularProgress sx={{ ml: 1 }} size={12} color="inherit" />}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
